import React, { useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import { VideoChooserBlock as Value } from "@reactivated";

import { VideoChooserBlock } from "@thelabnyc/thelabui/src/components/VideoChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { LogoLoader } from "../LogoLoader";
import { Svg } from "../Svg";
import { useDetectAudio } from "./utils";

import styles from "./index.module.scss";

export const VideoPlayerControls = ({
    value,
    wrapperAttrs,
    attrs,
    controlAttrs,
    type = "background",
}: {
    value: Value;
    wrapperAttrs?: React.HTMLAttributes<HTMLDivElement>;
    attrs?: React.HTMLAttributes<HTMLVideoElement>;
    controlAttrs?: React.HTMLAttributes<HTMLDivElement>;
    type?: null | ("background" | "foreground");
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    // Background videos are preloaded so that they can be autoplayed.
    // Foreground videos aren't loaded until the user interacts.
    const willPreload = type === "background";

    // Tracks whether or not the video loadeddata event has been called yet.
    const [isPlayable, setIsPlayable] = useState(false);

    // Tracks whether or not the video _should_ be playing.
    const [isPlaying, setIsPlaying] = useState(true);

    // Tracks whether or not the video is currently loading.
    const [isLoading, setIsLoading] = useState(willPreload);

    // Tracks whether or not the video is currently visible in the viewport.
    const [isVisible, setIsVisible] = useState(false);

    // Tracks whether or not the video should have sources yet.
    const [loadVideoSources, setLoadVideoSources] = useState(false);

    // Tracks whether the video is currently muted
    const [isMuted, setIsMuted] = useState(true);

    const toggleIsPlaying = () => {
        setIsPlaying((isPlaying) => !isPlaying);
    };

    /**
     * Manually set visibility of subtitles. If a track matches the active
     * locale it should be on by default, unless it's English. All other
     * tracks should be hidden.
     *
     * This overrides default browser behavior which seems to favor turning
     * on a subtitle by default if it's there as well as using the browser's/
     * system's language setting to determine the track.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        [...videoRef.current.textTracks].forEach((track) => {
            const trackLanguage = track.language.substring(0, 2);
            const activeLanguage = "en";
            if (trackLanguage === activeLanguage) {
                track.mode = "showing";
            } else {
                track.mode = "hidden";
            }
        });
    }, [isVisible]);

    /**
     * If this is a background video, autoplay it as soon as it's visible and
     * has finished loading.
     */
    useEffect(() => {
        if (!videoRef.current || type !== "background") {
            return;
        }
        setIsPlaying(!isLoading && isVisible && isPlayable);
    }, [type, isLoading, isVisible, isPlayable]);

    /**
     * When the isPlaying state changes, update the DOM state to match.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isPlayable && isPlaying) {
            void videoRef.current.play();
        } else {
            void videoRef.current.pause();
        }
    }, [isPlaying]);

    /**
     * Wait until after everything else is done loading to add sources to the
     * video element for page load/performance reasons.
     */
    useEffect(() => {
        const contentLoaded = () => {
            if (document.readyState === "complete") {
                setLoadVideoSources(true);
            }
        };
        document.addEventListener("readystatechange", contentLoaded);
        return () => {
            document.removeEventListener("readystatechange", contentLoaded);
        };
    }, []);

    /**
     * When the video element finishes loading metadata, set the playable boolean.
     */
    useEffect(() => {
        const setPlayable = () => {
            setIsPlayable(true);
        };
        const videoElem = videoRef.current;
        if (videoElem) {
            videoElem.addEventListener("loadeddata", setPlayable);
        }
        return () => {
            if (videoElem) {
                videoElem.removeEventListener("loadeddata", setPlayable);
            }
        };
    }, []);

    /**
     * Track if the video element is visible or not, and update the corresponding state bool.
     */
    useEffect(() => {
        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (!(entry.target instanceof HTMLVideoElement)) return;
                setIsVisible(entry.isIntersecting);
                // Once the video scrolls into view, load its sources.
                if (entry.isIntersecting) {
                    setLoadVideoSources(true);
                }
            });
        };
        const observer = new IntersectionObserver(callback, {
            threshold: 0.1,
        });
        const videoElem = videoRef.current;
        if (videoElem) {
            observer.observe(videoElem);
        }
        return () => {
            if (videoElem) {
                observer.unobserve(videoElem);
            }
        };
    }, []);

    const modifiedValue = {
        ...value,
        sources: loadVideoSources ? value.sources : [],
    };
    const hasAudio = useDetectAudio(videoRef, modifiedValue.sources);
    return (
        <div
            {...wrapperAttrs}
            className={concatClassNames([
                styles.root,
                wrapperAttrs?.className || "",
            ])}
        >
            {isLoading && (
                <>
                    <div className={styles.loaderOverlay} aria-hidden="true" />
                    <LogoLoader className={styles.loaderContainer} />
                </>
            )}
            <VideoChooserBlock
                ref={videoRef}
                attrs={{
                    loop: type === "background",
                    controls: type === "foreground",
                    preload: willPreload ? "auto" : "none",
                    autoPlay: true,
                    playsInline: true,
                    muted: isMuted,
                    onLoadStart: () => {
                        // When preload is set to "none", this still gets called
                        // right away, but none of the "loading finished" events
                        // will get called (since nothing is actually loading yet).
                        // Thus, only get isLoading is preload is enabled.
                        if (willPreload) {
                            setIsLoading(true);
                        }
                    },
                    onWaiting: () => {
                        setIsLoading(false);
                    },
                    onPlaying: () => {
                        setIsLoading(false);
                    },
                    onCanPlayThrough: () => {
                        setIsLoading(false);
                    },
                    onPause: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                    },
                    onPlay: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                    },
                    onVolumeChange: (e) => {
                        setIsMuted(e.currentTarget.muted);
                    },
                    ...attrs,
                }}
                value={modifiedValue}
            />
            <div
                className={concatClassNames([
                    type === "foreground"
                        ? styles.foregroundPlayControl
                        : styles.backgroundPlayControl,
                    isPlaying ? styles.playing : "",
                    controlAttrs?.className || "",
                ])}
            >
                <Clickable disabled={isLoading} onPress={toggleIsPlaying}>
                    <VisuallyHidden>
                        {isPlaying ? t`Pause Video` : t`Play Video`}
                    </VisuallyHidden>
                    <Svg
                        name={isPlaying ? "pause" : "play"}
                        className={styles.icon}
                        aria-hidden="true"
                    />
                </Clickable>
                {hasAudio && (
                    <Clickable
                        disabled={isLoading}
                        onPress={() => setIsMuted(!isMuted)}
                    >
                        <VisuallyHidden>
                            {isMuted ? "Unmute" : "Mute"} Video
                        </VisuallyHidden>
                        <Svg
                            name={isMuted ? "audio-off" : "audio-on"}
                            className={styles.icon}
                            aria-hidden="true"
                        />
                    </Clickable>
                )}
            </div>
        </div>
    );
};
